import { useMemo } from 'react';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import {
  Address,
  Project,
  ProjectStages,
  useAddress,
  useDeleteProject,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { EntityNames } from 'src/graphql/types';
import { Modals, useShowModals } from 'src/store/modals';
import { useConfirmationModal, useCopyToClipboard, useEntityFullUrl } from 'src/utils/hooks';

const LUCIDE_ICON_LIBRARY: IconLibrary = 'lucide';

export const useProjectActions = ({
  project,
  address,
}: {
  project: Project;
  address: Address;
}) => {
  const { confirm } = useConfirmationModal();
  const { copyToClipboard } = useCopyToClipboard();
  const { deleteProject } = useDeleteProject();
  const { updateAddress } = useAddress(address.addressId);
  const [openSubmitDay1Modal, openProposalWonModal, openProposalLostModal] = useShowModals(
    Modals.SubmitDay1,
    Modals.ProposalWon,
    Modals.ProposalLost,
  );

  const { groupRules } = useGroupRules();

  const enabledChangeProjectStatus = Boolean(
    groupRules[UserGroupRules.AccessToChangeProjectStatus],
  );

  const projectFullUrl = useEntityFullUrl(project.partnerId, EntityNames.Project, project.id);

  return useMemo(
    () => ({
      submitDay1: {
        label: 'Submit Day1',
        icon: 'calendar-check',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          project.projectStageId === ProjectStages.ActiveProject && enabledChangeProjectStatus,
        handler: () => openSubmitDay1Modal({ address, project }),
      },
      proposalWon: {
        label: 'Proposal won',
        icon: 'circle-check',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          project.projectStageId === ProjectStages.Proposal && enabledChangeProjectStatus,
        handler: () => openProposalWonModal({ address, project }),
      },
      proposalLost: {
        label: 'Proposal lost',
        icon: 'circle-x',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          ![ProjectStages.ServiceClient, ProjectStages.ProposalLost].includes(
            project.projectStageId,
          ) && enabledChangeProjectStatus,
        handler: () => openProposalLostModal({ address, project }),
      },
      copyProjectId: {
        label: 'Copy Project ID',
        icon: 'copy',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        handler: async () => {
          await copyToClipboard(project.id);
        },
      },
      copyProjectUrl: {
        label: 'Copy Project URL',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!projectFullUrl,
        handler: async () => {
          await copyToClipboard(projectFullUrl);
        },
      },
      deleteProject: {
        label: 'Delete',
        icon: 'trash-2',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        handler: async () => {
          const confirmed = await confirm({
            body: 'Please confirm you want \nto delete project from the site.',
            variant: 'delete',
          });
          if (confirmed) {
            if (address.primaryProjectId === project.id) {
              await updateAddress({
                ...address,
                primaryProjectId: null,
              });
            }
            const { errors } = await deleteProject(project.id);
            if (errors) {
              console.error(errors);
              return;
            }
          }
        },
      },
    }),
    [
      address,
      confirm,
      copyToClipboard,
      deleteProject,
      enabledChangeProjectStatus,
      openProposalLostModal,
      openProposalWonModal,
      openSubmitDay1Modal,
      project,
      projectFullUrl,
      updateAddress,
    ],
  );
};
